<script lang="ts">
  import { onMount } from "svelte"
  import { toast, xhr } from "$app/utils"
  import Loading from "$app/components/Loading.svelte"
  import Honeybadger from "$app/utils/honeybadger"
  import Tiptap from "$app/components/Tiptap.svelte"
  import { onlyTextButtons } from "$app/components/Tiptap/buttons"
  import { appStore } from "$app/store.svelte"

  type Props = {
    statement: StatementType
    section: StatementSectionType
    placeholder?: string
    title: string
    onupdate?: (data: StatementType) => void
    oncreate?: (data: StatementType) => void
    oncancel: () => void
  }

  let {
    statement,
    section,
    title = "",
    placeholder = "Enter a headline",
    oncreate = () => {},
    onupdate = () => {},
    oncancel
  }: Props = $props()

  const subtitle = {
    BrandPurpose: "Brand Purpose headline (< 5 words)",
    CoreValue: "Value headline (1 or 2 words)",
    BlueSkyVision: "Blue Sky Vision headline (< 5 words)",
    Strength: "Strength Title",
    Weakness: "Weakness Title",
    Opportunity: "Opportunity Title",
    Threat: "Threat Title"
  }[section]

  // if (section === "BrandPurpose") {
  //   subtitle = "Brand Purpose headline (< 5 words)"
  // } else if (section === "CoreValue") {
  //   subtitle = "Value headline (1 or 2 words)"
  // } else if (section === "BlueSkyVision") {
  //   subtitle = "Blue Sky Vision headline (< 5 words)"
  // } else if (section === "Strength") {
  //   placeholder = "Strength Title"
  // } else if (section === "Weakness") {
  //   placeholder = "Weakness Title"
  // } else if (section === "Opportunity") {
  //   placeholder = "Opportunity Title"
  // } else if (section === "Threat") {
  //   placeholder = "Threat Title"
  // }

  let form = { ...statement }
  let saving = $state(false)
  let errors: Record<string, string[]> = $state({})
  const btnTitle = statement.id ? "Update" : "Create"

  // Update statement
  async function updateStatement(): Promise<void | boolean> {
    saving = true

    try {
      let url = `/plans/${appStore.plan.id}/statements/${statement.id}.json`
      if (appStore.channel_id !== "") url += `?channel=${appStore.channel_id}`

      const resp = await xhr.put<StatementType>(url, {
        statement: {
          name: form.name,
          description: form.description
        }
      })
      appStore.trackStatement.status = "updated"
      onupdate(resp.data)
    } catch (e: any) {
      toast(`There was an error updating`, "error")
      if (e.response && e.response.data) {
        errors = { ...errors, ...e.response.data.errors }
      } else {
        if (typeof Honeybadger.notify !== "undefined") {
          Honeybadger.notify(e)
        }
      }
    } finally {
      saving = false
    }
  }

  async function createStatement() {
    saving = true

    try {
      const resp = await xhr.post<StatementType>(
        `/plans/${appStore.plan.id}/statements.json?channel=${appStore.channel_id}`,
        {
          statement: {
            name: form.name,
            description: form.description,
            section_type: section
          }
        }
      )
      appStore.trackStatement.status = "created"
      oncreate(resp.data.statement)
      appStore.onboarding = resp.data.onboarding
    } catch (e: any) {
      toast(`There was an error creating the ${title}`, "error")
      if (e.response && e.response.data) {
        errors = { ...errors, ...e.response.data.errors }
      } else {
        if (typeof Honeybadger.notify !== "undefined") {
          Honeybadger.notify(e)
        }
      }
    } finally {
      saving = false
    }
  }

  function focusInput(): void {
    const input = document.querySelector(
      "input[name=statement-name]"
    ) as HTMLInputElement
    if (input) {
      input.focus()
    }
  }
  appStore.trackStatement.edited =
    statement.name !== form.name || statement.description !== form.description

  onMount(() => {
    focusInput()
  })
</script>

<div>
  <h2 class="text-xl font-bold">{title}</h2>
  {#if subtitle.length > 0}
    <p class="mt-4 text-base">{subtitle}</p>
  {/if}
  <div class="mt-1 input-cont" class:error={errors.name}>
    <input
      type="text"
      bind:value={form.name}
      class="input flex-auto"
      name="statement-name"
      {placeholder}
    />
    <div class="msg">{errors.name}</div>
  </div>

  {#if !statement.id && ["BrandPurpose", "CoreValue", "BlueSkyVision"].includes(section)}
    <p class="mt-3 text-base italic">
      Note: You will be able to choose a symbol after creating your {title}
    </p>
  {/if}

  <p class="mt-4 text-base">Detailed Description</p>
  <div class="text-left mt-2 w-full inline-block">
    <div class="mt-1 h-48">
      <Tiptap
        content={statement.description}
        onupdate={({ html }) => (form.description = html)}
        placeholder={`Enter a description (optional)`}
        class="h-48"
        id="statement_description"
        buttons={onlyTextButtons}
        allowEmojis={true}
      />
    </div>
  </div>

  <div class="flex mt-4 justify-end">
    <Loading loading={saving}>
      <button
        class="btn primary ml-2"
        disabled={saving}
        onclick={() => {
          if (statement.id) {
            updateStatement()
          } else {
            createStatement()
          }
        }}
        data-name="button-save"
      >
        {btnTitle}
      </button>
    </Loading>
    <button
      class="btn secondary ml-2"
      disabled={saving}
      onclick={() => oncancel()}
      data-name="button-cancel"
    >
      Cancel
    </button>
  </div>
</div>
